import React from 'react'
import { useTranslation } from 'next-i18next'
import Box from '@mui/material/Box'
import { EmtCircularProgress } from 'emt-components-v2'
import { EmtChip, EmtIcon, EmtTypography, useMobile } from 'emt-components-v2'

type Props = React.PropsWithChildren<{
  data: {
    knowledgeScore: number
    psychologyScore: number
    isValidated: boolean
  }
}>

function getProgressColor(value: number) {
  if (value < 60) {
    return '#CCB243' // Yellow
  }
  return '#18BA92' // Green
}

export const TrustBadges: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation('petsitter')
  const [isMobile] = useMobile()

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          padding: '15px 18px',
          borderRadius: '12px',
          border: '1px solid #18BA92',
        }}
      >
        <EmtCircularProgress
          progressColor={getProgressColor(data.psychologyScore)}
          steps={false}
          value={data.psychologyScore}
        />
        <Box>
          <EmtTypography variant="body2" fontWeight="bold" color="#18BA92">
            {t('trustBadges.testPassed')}
          </EmtTypography>
          <EmtChip
            icon={
              <EmtIcon
                className="emt-psychology"
                sx={{ fontWeight: 100, fontSize: '10px!important', marginTop: '5px' }}
              />
            }
            color="pinklight"
            label={t('trustBadges.psychology')}
            size="medium"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              '& .MuiChip-label': { paddingLeft: '8px' },
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          padding: '15px 18px',
          borderRadius: '12px',
          border: '1px solid #18BA92',
        }}
      >
        <EmtCircularProgress
          progressColor={getProgressColor(data.knowledgeScore)}
          steps={false}
          value={data.knowledgeScore}
        />
        <Box>
          <EmtTypography variant="body2" fontWeight="bold" color="#18BA92">
            {t('trustBadges.testPassed')}
          </EmtTypography>
          <EmtChip
            icon={
              <EmtIcon
                className="emt-knowledge"
                sx={{ fontWeight: 100, fontSize: '10px!important', marginTop: '5px' }}
              />
            }
            color="infolight"
            label={t('trustBadges.competence')}
            size="medium"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              '& .MuiChip-label': { paddingLeft: '8px' },
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          padding: '15px 18px',
          borderRadius: '12px',
          border: '1px solid #18BA92',
        }}
      >
        <EmtIcon className="emt-face-id" sx={{ fontSize: '23px' }} />
        <Box>
          <EmtTypography variant="body2" fontWeight="bold" color="#18BA92">
            {t('trustBadges.identityVerified')}
          </EmtTypography>
          <EmtChip
            color="successlight"
            label={t('trustBadges.certified')}
            size="medium"
            sx={{
              marginTop: '5px',
              fontSize: '12px',
              fontWeight: 600,
              '& .MuiChip-label': { paddingLeft: '8px' },
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          padding: '15px 18px',
          borderRadius: '12px',
          border: data.isValidated ? '1px solid #18BA92' : '1px solid #E8E8EA',
        }}
      >
        <Box
          sx={{
            borderRadius: '50%',
            backgroundColor: data.isValidated ? '#D2FBF0' : '#E8E8EA',
            width: '45px',
            height: '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmtIcon
            className={data.isValidated ? 'emt-tick' : 'emt-hourglass'}
            sx={{ fontSize: '15px', color: data.isValidated ? '#18BA92' : '#615E69' }}
          />
        </Box>
        <Box>
          <EmtTypography variant="body2" fontWeight="bold" color={data.isValidated ? '#18BA92' : '#1D1929'}>
            {t('trustBadges.profileValidation')}
          </EmtTypography>
          <EmtTypography
            variant="body2"
            fontWeight="600"
            fontSize="12px"
            color={data.isValidated ? '#18BA92' : '#4A4754'}
          >
            {data.isValidated ? t('trustBadges.validatedProfile') : t('trustBadges.pending')}
          </EmtTypography>
        </Box>
      </Box>
    </Box>
  )
}
